import { ColumnDef } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";
import { Badge, Pagination, Table } from "@jhool-io/fe-components";
import {
    formatDate,
    makeStringFirstLetterCapital,
} from "../../../../utils/helpers";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import ListState from "../../../../components/ListState/ListState";
import Loader from "../../../../shared-ui/Loader/Loader";
import { CLIENT_TAG_DETAILS } from "../../helpers/clients.helpers";
import { ClientTagAction, IClientTagActivity } from "../../types/clients.types";
import { useFetchClientTagActivities } from "../../hooks/clients.queries";

interface ClientTagsHistoryProps {
    clientId: string;
}

const columns: ColumnDef<IClientTagActivity>[] = [
    {
        accessorKey: "created_at",
        header: "Date",
        cell: ({ row }) => formatDate(row.original.created_at),
    },
    {
        accessorKey: "client_tag_activity_id",
        header: "Time",
        cell: ({ row }) => formatDate(row.original.created_at, false, "h:mma"),
    },
    {
        accessorKey: "action",
        header: "Action",
        cell: ({ row }) => (
            <Badge
                type={
                    row.original.action === ClientTagAction.ADDED
                        ? "success"
                        : "danger"
                }
                content={row.original.action}
                className="capitalize"
            />
        ),
    },
    {
        accessorKey: "tag_key",
        header: "Profile tag",
        cell: ({ row }) => CLIENT_TAG_DETAILS[row.original.tag_key].label,
    },
    {
        accessorKey: "author_name",
        header: "Author",
    },
    {
        accessorKey: "origin_system",
        header: "Source",
        cell: ({ row }) =>
            makeStringFirstLetterCapital(row.original.origin_system),
    },
];

export default function ClientTagsHistory({
    clientId,
}: ClientTagsHistoryProps) {
    const [searchParams, setSearchParams] = useSearchParams();

    const pageFilter = Number(searchParams.get("tag_activity_page")) || 1;
    const limitFilter =
        Number(searchParams.get("tag_activity_limit")) || LISTS_DEFAULT_LIMIT;

    const { data, error, isLoading, isSuccess } = useFetchClientTagActivities(
        clientId,
        { page: pageFilter, limit: limitFilter },
        Boolean(clientId)
    );

    const handlePageChange = (page: number) => {
        searchParams.set("tag_activity_page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("tag_activity_limit", String(limit));
        setSearchParams(searchParams);
    };

    return (
        <>
            {isLoading ? (
                <Loader dataTestId="client-tags-history-loader" />
            ) : null}
            {error ? (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error.response?.data.message ||
                        "Cannot display profile tag history at this time please try again later"
                    }
                />
            ) : null}
            {data && isSuccess && data.data.length === 0 ? (
                <ListState
                    isError={false}
                    stateHelperText="Profile tags added or removed in Mantle or Intake will appear here"
                    emptyMessage="No profile tag history yet"
                />
            ) : null}
            {data && isSuccess && data.data.length > 0 ? (
                <Table
                    data={data.data}
                    columns={columns}
                    hasPagination={data.total_count > limitFilter}
                    pagination={
                        <Pagination
                            totalCount={data.total_count}
                            totalPages={data.total_pages}
                            currentPage={pageFilter}
                            onPageChange={handlePageChange}
                            onLimitChange={handleLimitChange}
                            limit={limitFilter}
                        />
                    }
                />
            ) : null}
        </>
    );
}
